import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Logo } from '@/components/Logo';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { LanguageToggle } from '@/components/LanguageToggle';
import { InstallButton, isStandalone } from '@/components/InstallPrompt';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Smartphone, Monitor, Share, Plus, QrCode, Copy, Check, Wifi, Zap, ShieldCheck, ArrowRight } from 'lucide-react';

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua) || (/Macintosh/i.test(ua) && 'ontouchend' in document)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

const Guide = ({ Icon, title, body, recommended, recommendedLabel, testId }) => (
  <div
    className="rounded-2xl p-4 border flex items-start gap-3"
    style={{
      background: 'var(--surface)',
      borderColor: recommended ? 'var(--primary)' : 'var(--border)',
      boxShadow: recommended ? 'var(--shadow)' : 'none',
    }}
    data-testid={testId}
  >
    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--surface-alt)' }}>
      <Icon size={20} style={{ color: 'var(--primary)' }} />
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-bold" style={{ color: 'var(--text)' }}>{title}</h3>
        {recommended && (
          <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: 'color-mix(in srgb, var(--primary) 16%, transparent)', color: 'var(--primary)' }}>
            {recommendedLabel}
          </span>
        )}
      </div>
      <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{body}</p>
    </div>
  </div>
);

export default function Install() {
  const navigate = useNavigate();
  const t = useT();
  const [platform] = useState(detectPlatform());
  const [copied, setCopied] = useState(false);

  const installUrl = `${window.location.origin}/install`;

  useEffect(() => { api.track('install_page_viewed', { platform }); }, [platform]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(installUrl);
      setCopied(true);
      toast.success(t('ins_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('ins_copy_fail'));
    }
  };

  const guides = {
    android: { Icon: Smartphone, title: t('ins_android_t'), body: t('ins_android_b'), testId: 'install-guide-android' },
    ios: { Icon: Share, title: t('ins_ios_t'), body: t('ins_ios_b'), testId: 'install-guide-ios' },
    desktop: { Icon: Monitor, title: t('ins_desktop_t'), body: t('ins_desktop_b'), testId: 'install-guide-desktop' },
  };
  const order = platform === 'ios' ? ['ios', 'android', 'desktop']
    : platform === 'android' ? ['android', 'ios', 'desktop']
    : ['desktop', 'android', 'ios'];

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <div className="sticky top-0 z-30" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="mx-auto max-w-3xl px-4 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} aria-label="KilalaRide home"><Logo /></button>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <div className="hidden sm:block"><ThemeSwitcher compact /></div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 ss-fade-in">
        {/* Hero */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
            <ShieldCheck size={34} strokeWidth={2.3} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: 'var(--text)' }} data-testid="install-title">{t('ins_title')}</h1>
          <p className="text-sm sm:text-base mt-2 max-w-xl mx-auto" style={{ color: 'var(--text-muted)' }}>{t('ins_sub')}</p>
        </div>

        {isStandalone() ? (
          <div className="rounded-2xl p-5 border text-center mb-6" style={{ background: 'var(--surface)', borderColor: 'var(--primary)' }} data-testid="install-already">
            <Check size={28} style={{ color: 'var(--success)' }} className="mx-auto mb-2" />
            <p className="font-bold" style={{ color: 'var(--text)' }}>{t('ins_already_t')}</p>
            <button onClick={() => navigate('/app')} className="mt-3 inline-flex items-center gap-2 rounded-full px-5 min-h-[48px] font-bold" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="install-open-app">
              {t('ins_open_app')} <ArrowRight size={18} />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center mb-6">
            <InstallButton className="w-full sm:w-auto !min-h-[54px] !px-8 text-base" label={t('ins_cta')} testid="install-page-button" />
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {/* QR card */}
          <div className="rounded-2xl p-5 border flex flex-col items-center text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="install-qr-card">
            <div className="flex items-center gap-2 mb-1">
              <QrCode size={18} style={{ color: 'var(--primary)' }} />
              <h3 className="font-bold" style={{ color: 'var(--text)' }}>{t('ins_scan_t')}</h3>
            </div>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>{t('ins_scan_b')}</p>
            <div className="rounded-xl p-3" style={{ background: '#ffffff' }}>
              <QRCodeSVG value={installUrl} size={168} level="M" includeMargin={false} fgColor="#0F766E" bgColor="#ffffff" data-testid="install-qr-svg" />
            </div>
            <button
              onClick={copyLink}
              className="mt-4 inline-flex items-center gap-2 rounded-full px-4 min-h-[44px] text-sm font-semibold border transition-colors"
              style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }}
              data-testid="install-copy-link"
            >
              {copied ? <Check size={16} style={{ color: 'var(--success)' }} /> : <Copy size={16} style={{ color: 'var(--primary)' }} />}
              {copied ? t('ins_copied') : t('ins_copy')}
            </button>
          </div>

          {/* Why install */}
          <div className="rounded-2xl p-5 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <h3 className="font-bold mb-3" style={{ color: 'var(--text)' }}>{t('ins_why_t')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm" style={{ color: 'var(--text)' }}><Zap size={18} style={{ color: 'var(--primary)' }} /> {t('ins_why1')}</li>
              <li className="flex items-center gap-3 text-sm" style={{ color: 'var(--text)' }}><Wifi size={18} style={{ color: 'var(--primary)' }} /> {t('ins_why2')}</li>
              <li className="flex items-center gap-3 text-sm" style={{ color: 'var(--text)' }}><ShieldCheck size={18} style={{ color: 'var(--primary)' }} /> {t('ins_why3')}</li>
            </ul>
          </div>
        </div>

        {/* Per-device guides */}
        <div className="space-y-3 mb-6">
          <h2 className="font-bold text-lg" style={{ color: 'var(--text)' }}>{t('ins_how_t')}</h2>
          {order.map((key) => (
            <Guide
              key={key}
              Icon={guides[key].Icon}
              title={guides[key].title}
              body={guides[key].body}
              recommended={key === platform}
              recommendedLabel={t('ins_recommended')}
              testId={guides[key].testId}
            />
          ))}
        </div>

        <div className="text-center">
          <button onClick={() => navigate('/app')} className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: 'var(--primary)' }} data-testid="install-open-browser">
            {t('ins_open')} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
